import { useRef, useEffect } from 'react';
import { useTheme } from '../context/ThemeContext';

export default function MyThemeView({ onClose }) {
  const { theme, setTheme } = useTheme();
  const viewRef = useRef(null);
  
  // Position the view when it's mounted
  useEffect(() => {
    const positionView = () => {
      // Find position of the trigger
      const themeTrigger = document.querySelector('[data-theme-trigger]');
      if (themeTrigger && viewRef.current) {
        const rect = themeTrigger.getBoundingClientRect();
        
        // Wait for the view to be fully rendered to get its height
        setTimeout(() => {
          if (viewRef.current) {
            // Get the height of the view
            const viewHeight = viewRef.current.offsetHeight;
            const viewWidth = viewRef.current.offsetWidth;
            
            // Calculate the position to align the top of the view with the top of the trigger
            let topPosition = rect.top;
            let leftPosition = rect.right + 8;
            
            // Check if the popup would go off the right edge of the viewport
            if (leftPosition + viewWidth > window.innerWidth) {
              // Position to the left of the trigger instead
              leftPosition = rect.left - viewWidth - 8;
            }
            
            // Make sure the view doesn't go above the viewport
            if (topPosition < 8) {
              topPosition = 8;
            }
            
            // Make sure the view doesn't go below the viewport
            if (topPosition + viewHeight > window.innerHeight - 8) {
              topPosition = window.innerHeight - viewHeight - 8;
            }
            
            // Make sure left position is not negative
            if (leftPosition < 8) {
              leftPosition = 8;
            }
            
            viewRef.current.style.top = `${topPosition}px`;
            viewRef.current.style.left = `${leftPosition}px`;
          }
        }, 10);
      }
    };
    
    positionView();
    
    // In case the window is resized
    window.addEventListener('resize', positionView);
    return () => {
      window.removeEventListener('resize', positionView);
    };
  }, []);
  
  // Close when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      // Check if the click is outside of the themeView
      if (viewRef.current && !viewRef.current.contains(event.target) && 
          !event.target.closest('[data-theme-trigger]')) {
        onClose();
      }
    }
    
    // Use capture phase to ensure we get the event first
    document.addEventListener("mousedown", handleClickOutside, true);
    
    // Add escape key handler
    const handleEscKey = (event) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };
    
    document.addEventListener("keydown", handleEscKey);
    
    return () => {
      document.removeEventListener("mousedown", handleClickOutside, true);
      document.removeEventListener("keydown", handleEscKey);
    };
  }, [onClose]);

  // Theme options
  const themes = [
    {
      id: 'light',
      name: 'Light (Default)',
      color: 'bg-blue-600',
    },
    {
      id: 'dark',
      name: 'Dark',
      color: 'bg-gray-800',
    },
  ];

  return (
    <div 
      ref={viewRef}
      className="fixed w-48 bg-white dark:bg-dark-card rounded-md shadow-xl border border-gray-200 dark:border-dark-border overflow-hidden z-[200]"
      style={{
        // Initial position will be set by useEffect
        left: '-1000px', // Start off-screen until properly positioned
        top: '0'
      }} 
      data-theme-view
      onMouseEnter={(e) => {
        // Ensure the view stays visible when hovered
        e.stopPropagation();
      }}
      onMouseLeave={(e) => {
        const themeTrigger = document.querySelector('[data-theme-trigger]');
        if (!themeTrigger?.matches(':hover')) {
          onClose();
        }
      }}
    >
      {/* Content */}
      <div className="p-2">
        <div className="text-xs font-semibold mb-2 text-gray-700 dark:text-dark-text">Select Theme</div>
        <div className="space-y-0.5">
          {themes.map((themeOption) => (
            <div 
              key={themeOption.id}
              tabIndex={0}
              role="button"
              data-theme-item
              className={`flex items-center p-1.5 hover:bg-blue-50 dark:hover:bg-gray-700 rounded cursor-pointer ${
                theme === themeOption.id ? 'bg-blue-50 dark:bg-gray-700' : ''
              }`}
              onClick={() => {
                setTheme(themeOption.id);
                onClose();
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setTheme(themeOption.id);
                  onClose();
                }
              }}
            >
              <div className={`w-4 h-4 ${themeOption.color} rounded-full mr-2`}></div>
              <div className="text-xs text-gray-800 dark:text-dark-text">{themeOption.name}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
